import { useEffect, useState, useCallback } from 'react';
import { getStatus, getBalance, getStats, getPositions, getTrades, getKillSwitch } from '../api';
import type { StatusResponse, BalanceResponse, StatsResponse, PositionsResponse, TradesResponse, KillSwitchInfo } from '../types';
import StatCard from '../components/StatCard';
import KillSwitchComponent from '../components/KillSwitch';
import PositionTable from '../components/PositionTable';
import TradeTable from '../components/TradeTable';
import LogViewer from '../components/LogViewer';

export default function Overview() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [positions, setPositions] = useState<PositionsResponse | null>(null);
  const [trades, setTrades] = useState<TradesResponse | null>(null);
  const [killSwitch, setKillSwitch] = useState<KillSwitchInfo | null>(null);

  const refresh = useCallback(async () => {
    const [s, b, st, p, t, k] = await Promise.all([
      getStatus().catch(() => null),
      getBalance().catch(() => null),
      getStats().catch(() => null),
      getPositions().catch(() => null),
      getTrades(10).catch(() => null),
      getKillSwitch().catch(() => null),
    ]);
    if (s) setStatus(s);
    if (b) setBalance(b);
    if (st) setStats(st);
    if (p) setPositions(p);
    if (t) setTrades(t);
    if (k) setKillSwitch(k);
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const formatUptime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Overview</h2>
        <span className="text-xs text-trader-muted">Auto-refresh: 5s</span>
      </div>

      {/* Stat cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Net PnL"
          value={balance ? `$${balance.net_pnl.toFixed(4)}` : '—'}
          variant={balance ? (balance.net_pnl >= 0 ? 'profit' : 'loss') : 'default'}
          sub={balance ? `Fees: $${balance.total_fees.toFixed(4)}` : undefined}
        />
        <StatCard
          label="Total Trades"
          value={stats?.total_trades ?? '—'}
          variant="info"
          sub={stats ? `${stats.filled_trades} filled` : undefined}
        />
        <StatCard
          label="Open Positions"
          value={positions?.count ?? '—'}
          variant={positions && positions.count > 0 ? 'warn' : 'default'}
        />
        <StatCard
          label="Uptime"
          value={status ? formatUptime(status.uptime_seconds) : '—'}
          variant="default"
          sub={status?.db.latest_trade ? `Last trade: ${status.db.latest_trade}` : 'No trades'}
        />
      </div>

      {/* Kill switch + positions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div>
          {killSwitch && <KillSwitchComponent info={killSwitch} onUpdate={refresh} />}
        </div>
        <div className="lg:col-span-2 bg-trader-card border border-trader-border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3">Open Positions</h3>
          <PositionTable positions={positions?.positions ?? []} />
        </div>
      </div>

      {/* Recent trades */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Recent Trades</h3>
        <TradeTable trades={trades?.trades ?? []} />
      </div>

      {/* Log viewer */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Live Logs</h3>
        <LogViewer maxLines={100} />
      </div>
    </div>
  );
}
